import {
  Button,
  ModalityCodesEnum,
  Select,
  SelectOptionType,
  TextInput,
  addToast,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { getCoursesListRequest } from '../../../api/requests/sectionsMaintainer';
import DisplayError from '../../../components/info/DisplayError';
import { SectionTitle } from '../../../components/text';
import { useSearchAssistant } from '../../../hooks/remoteSearchSelect/useSearchAssistant';
import { useSearchTeachers } from '../../../hooks/remoteSearchSelect/useSearchTeachers';
import { useLoadingState } from '../../../hooks/useLoadingState';
import { useValidations } from '../../../hooks/useValidations';
import { Campus } from '../../../types/campus';
import { Schedule } from '../../../types/schedule';
import {
  CourseType,
  FormDataResponseType,
  SchoolType,
} from '../../../types/sectionMaintainerTypes';
import { UpdateTeacherOrAssistantRow } from '../../SectionsMaintainer/types';
import ExtraFields from '../../SectionsMaintainer/parts/ExtraFields';
import { createContinuousEducationSectionRequest } from '../api';

export type ContinuousEducationSectionValuesForm = {
  faculty: SelectOptionType;
  course: SelectOptionType;
  courseName: SelectOptionType;
  name: string;
  sectionCode: string;
  campus: SelectOptionType;
  schedule: SelectOptionType;
  quota: string;
  teachers: UpdateTeacherOrAssistantRow[];
  assistants: UpdateTeacherOrAssistantRow[];
};

interface Props {
  onCancel: () => void;
  onSuccess: () => void;
  formData: FormDataResponseType | undefined;
  loading: boolean;
  error?: any;
  retry: () => void;
}

const ContinuousEducationSectionForm = ({
  onCancel,
  onSuccess,
  formData,
  loading: loadingFormData,
  error: errorFormData,
  retry,
}: Props) => {
  const { t } = useTranslation();
  const prefix = 'sectionsMaintainer.create';

  const { msgValidations, rulesPatternNumeric } = useValidations();
  const {
    loading: loadingCourses,
    setLoading: setLoadingCourses,
  } = useLoadingState();
  const searchTeachers = useSearchTeachers();
  const searchAssistants = useSearchAssistant();

  const facultyOptions = useMemo(() => {
    return (
      formData?.schools.map((row: SchoolType) => ({
        value: row.id,
        label: row.name,
      })) || []
    );
  }, [formData]);

  const campusOptions = useMemo(() => {
    return (
      formData?.campuses.map((row: Campus) => ({
        value: row.id,
        label: row.name,
      })) || []
    );
  }, [formData]);

  const schedulesOptions = useMemo(() => {
    return (
      formData?.schedules.map((row: Schedule) => ({
        value: row.id ?? '',
        label: row.name,
      })) || []
    );
  }, [formData]);

  const [coursesOptions, setCoursesOptions] = useState<SelectOptionType[]>([]);
  const [courseNamesOptions, setCourseNamesOptions] = useState<
    SelectOptionType[]
  >([]);
  const [courses, setCourses] = useState<CourseType[]>([]);

  const methods = useForm<ContinuousEducationSectionValuesForm>({
    defaultValues: {
      teachers: [
        {
          value: 0,
          label: '',
          isOnline: false,
          isPresential: false,
          order: 0,
        },
      ],
      assistants: [
        {
          value: 0,
          label: '',
          isOnline: false,
          isPresential: false,
          order: 0,
        },
      ],
    },
  });

  const { control, setValue } = methods;
  const [selectedFaculty, selectedCourse] = useWatch({
    control,
    name: ['faculty', 'course'],
  });

  const currentCourse = useMemo(
    () => courses.find((c) => c.code === selectedCourse?.value),
    [courses, selectedCourse],
  );

  const currentDeliveryMode = useMemo(
    () =>
      currentCourse
        ? (currentCourse as any)?.modality?.deliveryMode || ModalityCodesEnum.PR
        : ModalityCodesEnum.PR,
    [currentCourse],
  );

  const supportsPresential = useMemo(
    () =>
      !!currentDeliveryMode &&
      [
        ModalityCodesEnum.PR,
        ModalityCodesEnum.SP,
        ModalityCodesEnum.DU,
      ].includes(currentDeliveryMode),
    [currentDeliveryMode],
  );

  const supportsOnline = useMemo(
    () =>
      !!currentDeliveryMode &&
      [
        ModalityCodesEnum.OL,
        ModalityCodesEnum.SP,
        ModalityCodesEnum.DI,
      ].includes(currentDeliveryMode),
    [currentDeliveryMode],
  );

  const loadCourses = useCallback(
    async (facultyId?: number) => {
      setLoadingCourses(true);
      const { data } = await getCoursesListRequest(facultyId);
      if (data) {
        setCourses(data);
        setCoursesOptions(
          data.map((row: CourseType) => ({
            value: row.code,
            label: row.code,
            name: row.name,
          })),
        );
        setCourseNamesOptions(
          data.map((row: CourseType) => ({
            value: row.name,
            label: row.name,
            code: row.code,
          })),
        );
      }
      setLoadingCourses(false);
    },
    [setLoadingCourses],
  );

  useEffect(() => {
    if (selectedFaculty?.value) {
      loadCourses(Number(selectedFaculty.value));
      setValue('course', null as any);
      setValue('courseName', null as any);
    } else {
      loadCourses();
    }
  }, [selectedFaculty, loadCourses, setValue]);

  const onCourseChange = (option: SelectOptionType) => {
    setValue('course', option);
    if (option?.value) {
      const courseName = courseNamesOptions.find(
        (c) => (c as any).code === option.value,
      );
      if (courseName) {
        setValue('courseName', courseName);
      }
    } else {
      setValue('courseName', null as any);
    }
  };

  const onCourseNameChange = (option: SelectOptionType) => {
    setValue('courseName', option);
    if (option?.value) {
      const course = coursesOptions.find(
        (c) => c.value === (option as any).code,
      );
      if (course) {
        setValue('course', course);
      }
    } else {
      setValue('course', null as any);
    }
  };

  const onSubmit = async (values: ContinuousEducationSectionValuesForm) => {
    const activePeriod = formData?.periods?.find((p) => p.active);

    if (!activePeriod) {
      addToast({ text: t('common.error'), color: 'danger' });
      return;
    }

    const { data, error } = await createContinuousEducationSectionRequest({
      maximumStudent: Number(values.quota),
      campusId: Number(values.campus.value),
      courseCode: values.course.value.toString(),
      periodId: activePeriod.id,
      name: `${values.sectionCode}-${values.name}`,
      scheduleId: values.schedule?.value
        ? Number(values.schedule.value)
        : undefined,
      professors: values.teachers.filter((item) => !!item.value),
      assistants: values.assistants.filter((item) => !!item.value),
    });

    if (data && !error) {
      addToast({ text: t('common.saveSuccess'), color: 'success' });
      onSuccess();
    } else {
      const errorMessage = (error as any)?.data?.message;
      addToast({
        text: errorMessage || t('common.saveError'),
        color: 'danger',
      });
    }
  };

  if (errorFormData) {
    return (
      <DisplayError textBody={errorFormData.toString()} retryAction={retry} />
    );
  }

  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(onSubmit)}>
        <Row className="pb-4">
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.generalSection`)} />
            <p className="fs-16">
              {t(`${prefix}.continuousEducationSectionDescription`)}
            </p>
          </Col>

          <Col xs={12} md={3}>
            <Select
              name="faculty"
              label={t('sectionsMaintainer.create.facultyLabel')}
              options={facultyOptions}
              control={control}
              rules={{ required: msgValidations.required }}
              disabled={loadingFormData}
            />
          </Col>

          <Col xs={12} md={3}>
            <Controller
              control={control}
              name="course"
              rules={{ required: msgValidations.required }}
              render={({ field: { onChange, value, ref, ...field } }) => (
                <Select
                  {...field}
                  value={value}
                  label={t('sectionsMaintainer.create.courseLabel')}
                  options={coursesOptions}
                  isSearchable
                  disabled={loadingCourses}
                  onChange={(val: any) => {
                    onChange(val);
                    onCourseChange(val);
                  }}
                />
              )}
            />
          </Col>

          <Col xs={12} md={3}>
            <Controller
              control={control}
              name="courseName"
              rules={{ required: msgValidations.required }}
              render={({ field: { onChange, value, ref, ...field } }) => (
                <Select
                  {...field}
                  value={value}
                  label={t(`${prefix}.coursesLabel`)}
                  options={courseNamesOptions}
                  isSearchable
                  disabled={loadingCourses}
                  onChange={(val: any) => {
                    onChange(val);
                    onCourseNameChange(val);
                  }}
                />
              )}
            />
          </Col>

          <Col xs={12} md={3}>
            <TextInput
              name="name"
              label={t(`${prefix}.sectionNameLabel`)}
              control={control}
              rules={{ required: msgValidations.required }}
            />
          </Col>

          <Col xs={12} md={3}>
            <TextInput
              name="sectionCode"
              label={t(`${prefix}.sectionCodeLabel`)}
              control={control}
              rules={{ required: msgValidations.required }}
            />
          </Col>

          <Col xs={12} md={3}>
            <Select
              name="campus"
              label={t('sectionsMaintainer.create.campusLabel')}
              options={campusOptions}
              control={control}
              rules={{ required: msgValidations.required }}
              disabled={loadingFormData}
            />
          </Col>

          <Col xs={12} md={3}>
            <Select
              name="schedule"
              label={t(`${prefix}.schedulesLabel`)}
              options={schedulesOptions}
              control={control}
              rules={{ required: msgValidations.required }}
              disabled={loadingFormData}
            />
          </Col>

          <Col xs={12} md={3}>
            <TextInput
              name="quota"
              label={t('sectionsMaintainer.create.quotaLabel')}
              control={control}
              rules={{
                required: msgValidations.required,
                pattern: rulesPatternNumeric,
              }}
            />
          </Col>
        </Row>

        <Row className="pb-5">
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.teacherTitle`)} />
          </Col>
          <Col xs={12} md={6}>
            <ExtraFields
              title="teachers"
              inputLabel={t(`${prefix}.teacher`)}
              search={searchTeachers}
              buttonLabel={t(`${prefix}.addTeacher`)}
              supportsOnline={supportsOnline}
              supportsPresential={supportsPresential}
            />
          </Col>
          <Col xs={12} md={6}>
            <ExtraFields
              title="assistants"
              inputLabel={t(`${prefix}.assistant`)}
              search={searchAssistants}
              buttonLabel={t(`${prefix}.addAssistant`)}
              supportsOnline={supportsOnline}
              supportsPresential={supportsPresential}
            />
          </Col>
        </Row>

        <Row className="py-5 justify-content-end pt-5">
          <Col className="" xs={12} lg={3}>
            <Button
              outlined
              color="secondary"
              onClick={onCancel}
              className="mr-2"
              fullwidth
              text={t('common.terms.cancel')}
            />
          </Col>
          <Col xs={12} lg={3} className="pb-2 order-2 order-lg-1">
            <Button
              color="primary"
              type="submit"
              fullwidth
              text="+ Crear y continuar"
            />
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
};

export default ContinuousEducationSectionForm;
